import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import { translations } from 'src/constants/translations';
import { IStatus, IComplex } from 'src/typings/complexes';
import { TypeOfRole } from 'src/typings/user';
import { useGetComplexStatuses } from 'src/hooks';
import { requestSelectors } from 'src/store/requests/selectors';
import { appIcon } from 'src/assets';
import { borderColor } from 'src/constants/styles';
import { PlacementShowModule } from 'src/typings/filter';
import { StatusModal } from 'src/components/profileComponents/EditComplex/components/StatusModal';
import { useGlobalGrid } from 'src/hooks/app/use-global-grid';
import { useStyles as useCommonUsers } from 'src/components/profileComponents/Common';
import { useStyles } from './styles';

interface IComplexStatuses {
  complex: IComplex;
  addStatusTrigger: boolean;
  close?: (value: boolean) => void;
  search: string;
}

export const ComplexStatuses: React.FC<IComplexStatuses> = ({
  complex, addStatusTrigger, search, close,
}) => {
  const { id }: { id: string } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [editedStatus, setEditedStatus] = useState<IStatus|null>(null);
  const { statuses, fetchStatuses } = useGetComplexStatuses(id);
  const { getGridsWidth } = useGlobalGrid();
  const styles = useStyles(getGridsWidth);
  const commonUsersStyles = useCommonUsers();
  const user = useSelector(requestSelectors.user.getUserData);
  const lang = complex.language;

  useEffect(() => {
    if (addStatusTrigger) handleAddStatus();
  }, [addStatusTrigger]);

  const handleEditStatus = (status: IStatus) => {
    setEditedStatus(status);
    setModalOpen(true);
  };
  const handleAddStatus = () => {
    setEditedStatus(null);
    setModalOpen(true);
  };

  const refreshStatuses = () => {
    setModalOpen(false);
    fetchStatuses();
  };

  const onClose = (value: boolean) => {
    setModalOpen(value);
    close?.(value);
  };

  const searchFilter = useCallback((item: IStatus) => {
    if (!search.length) return true;
    return [
      item.name[lang].toLowerCase(),
      item.crosstableName[lang].toLowerCase(),
      item.moduleName[lang].toLowerCase(),
    ].join()
      .includes(search.toLowerCase());
  }, [lang, search]);

  return (
    <Box>
      <Box width="100%">
        <Box
          display="inline-flex"
          justifyContent="flex-start"
          alignItems="center"
          pb={1}
          fontSize="16px"
          fontWeight={500}
          lineHeight="20px"
          borderBottom={`1px solid ${borderColor}`}
          width="100%"
        >
          <Box minWidth={`${getGridsWidth(1)}px`} pl="12px">
            {translations.systemPlacement}
          </Box>
          <Box minWidth={`${getGridsWidth(1)}px`}>
            {translations.crmAdminStatus}
          </Box>
          <Box minWidth={`${getGridsWidth(1)}px`}>
            {translations.websiteStatus}
          </Box>
          <Box minWidth={`${getGridsWidth(1)}px`} />
          <Box minWidth={`${getGridsWidth(1)}px`} display="flex" justifyContent="center">
            {translations.showOnWebsite}
          </Box>
          <Box minWidth={`${getGridsWidth(1)}px`} display="flex" justifyContent="center">
            {translations.canAttachUnitInDeal}
          </Box>
        </Box>
        <Box width="100%">
          {statuses && statuses
            .filter(searchFilter)
            .map((item) => (
              <Box
                display="inline-flex"
                justifyContent="flex-start"
                alignItems="center"
                key={item.id}
                width="100%"
                className={styles.cell}
              >
                <Box
                  className={styles.textCell}
                  minWidth={`${getGridsWidth(1)}px`}
                >
                  <IconButton
                    color="primary"
                    disabled={user?.role?.name === TypeOfRole.manager}
                    onClick={() => handleEditStatus(item)}
                    size="large"
                  >
                    <img className="edit-icon" src={appIcon.iconEdit.default} alt="edit statuses" />
                  </IconButton>
                  <Box flex="1">
                    {item.name && item.name[lang]}
                  </Box>
                </Box>
                <Box
                  className={styles.textCell}
                  minWidth={`${getGridsWidth(1)}px`}
                >
                  <Box
                    width="20px"
                    height="20px"
                    mr="8px"
                    borderRadius="5px"
                    title={translations.colorInCross}
                    style={{
                      backgroundColor: item.crosstableColor || '#fff',
                    }}
                  />
                  <Box flex="1">
                    {item.crosstableName && item.crosstableName[lang]}
                  </Box>
                </Box>
                <Box
                  className={styles.textCell}
                  minWidth={`${getGridsWidth(1)}px`}
                >
                  <Box
                    width="20px"
                    height="20px"
                    mr="8px"
                    borderRadius="5px"
                    title={translations.colorInWebsite}
                    style={{
                      backgroundColor: item.color,
                    }}
                  />
                  <Box flex="1">
                    {item.moduleName[lang]}
                  </Box>
                </Box>
                <Box
                  className={styles.textCell}
                  minWidth={`${getGridsWidth(1)}px`}
                />
                <Box
                  className={styles.checkboxCell}
                  minWidth={`${getGridsWidth(1)}px`}
                >
                  <Checkbox
                    className={commonUsersStyles.checkbox}
                    style={{
                      cursor: 'default',
                    }}
                    color="primary"
                    checked={item.showInModule === PlacementShowModule.show}
                  />
                </Box>
                <Box
                  className={styles.checkboxCell}
                  minWidth={`${getGridsWidth(1)}px`}
                >
                  <Checkbox
                    className={commonUsersStyles.checkbox}
                    style={{
                      cursor: 'default',
                    }}
                    color="primary"
                    checked={item.amoCanBeSetForDeal}
                  />
                </Box>
              </Box>
            ))}
        </Box>
      </Box>
      {modalOpen && (
        <StatusModal
          isOpen={modalOpen}
          handleChangeOpen={onClose}
          refreshStatuses={refreshStatuses}
          status={editedStatus}
          complex={complex}
        />
      )}
    </Box>
  );
};
